import * as Dialog from '@radix-ui/react-dialog'
import { ExpandableDrawer, DrawerState } from '@Components/expandable-drawer'
import { AddonResultDetails, ListResult } from '../list-view/list-view'
import { useCallback, useState } from 'react'
import { TicketInfo } from '@Config/ticket-form-types'
import { TicketStatusPillWithWinnings } from '../ticket-status-pill/ticket-status-pill'
import { Utils } from '@Util/utils'
import { ReceiptBreakdownNumberSummary } from './receipt-breakdown-number-summary'
import { ReceiptBreakdownMiniGameSummary } from './receipt-breakdown-mini-game-summary'
import { AddOnTypes } from '@Config/types'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'

export type ReceiptBreakdownDrawerProps = {
  kenoResult: ListResult
  ticketInfo: TicketInfo
  addonDetails: AddonResultDetails[]
  onOpenChange: (open: boolean) => void
}

const CLASS_NAME = `ticket-list-view`

export const ReceiptBreakdownDrawer = ({
  addonDetails,
  kenoResult,
  onOpenChange,
  ticketInfo,
}: ReceiptBreakdownDrawerProps) => {
  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)
  const [drawerState, setDrawerState] = useState<DrawerState>(DrawerState.OPEN)

  const onDrawerStateChange = (open: boolean) => {
    setDrawerState(open ? DrawerState.OPEN : DrawerState.CLOSED)
    onOpenChange(open)
  }

  const addonsForSection = useCallback(() => {
    return addonDetails.filter((x) => x.type === AddOnTypes.EXTRA_DRAWS)
  }, [addonDetails])

  return (
    <Dialog.Root open={!!kenoResult} onOpenChange={onDrawerStateChange}>
      <ExpandableDrawer
        classPrefix={`${CLASS_NAME}__receipt-breakdown`}
        hasCloseButton={false}
        state={drawerState}
        scrollable={false}
        onOpenChange={onDrawerStateChange}>
        <div className={`${CLASS_NAME}__receipt-breakdown`}>
          <div className={`${CLASS_NAME}__receipt-breakdown__header`}>
            <div>{kenoResult.drawNumber}</div>
            <div className={`${CLASS_NAME}__receipt-breakdown__header__divider`}>{' \u2022 '}</div>
            <div>{Utils.toShortDate(kenoResult.drawDate || '')}</div>
          </div>

          <ReceiptBreakdownNumberSummary
            addonDetails={addonsForSection()}
            className={CLASS_NAME}
            kenoResult={kenoResult}
          />

          <div className={`${CLASS_NAME}__receipt-breakdown__container`}>
            <div className={`${CLASS_NAME}__receipt-breakdown__section`}>
              <div className={`${CLASS_NAME}__receipt-breakdown__section__column`}>
                {kenoResult.scoringBreakdown?.baseWinnings.spot} Spot
              </div>
              <div className={`${CLASS_NAME}__receipt-breakdown__section__column`}>
                <div className={`${CLASS_NAME}__receipt-breakdown__section__column__center`}>
                  Match {kenoResult.scoringBreakdown?.baseWinnings.numberMatched} of{' '}
                  {kenoResult.scoringBreakdown?.baseWinnings.spot}
                </div>
                <div>
                  {Utils.convertCentsNumberToDollarString(
                    kenoResult.scoringBreakdown?.baseWinnings.winningsInCents || 0,
                  )}
                </div>
              </div>
            </div>

            <div className={`${CLASS_NAME}__receipt-breakdown__section__addon-details`}>
              {Array.from(addonsForSection()).map((x) => {
                return <div key={x.name}>with {x.name}</div>
              })}
            </div>

            {kenoResult.scoringBreakdown?.multiplierAddonWinnings?.length ? (
              <>
                <hr />
                {kenoResult.scoringBreakdown?.multiplierAddonWinnings.map((maw) => {
                  return (
                    <div
                      key={maw.addonName}
                      className={`${CLASS_NAME}__receipt-breakdown__section`}>
                      <div className={`${CLASS_NAME}__receipt-breakdown__section__column`}>
                        {maw.addonName}
                      </div>
                      <div className={`${CLASS_NAME}__receipt-breakdown__section__column`}>
                        <div
                          className={`${CLASS_NAME}__receipt-breakdown__section__column__center`}>
                          {Utils.getMultiplierDisplayValue(
                            maw.multiplier,
                            hostConfig?.multiplierConfig.noMultiplierDisplayString,
                            true,
                          )}
                        </div>
                        <div>{Utils.convertCentsNumberToDollarString(maw.winningsInCents)}</div>
                      </div>
                    </div>
                  )
                })}
              </>
            ) : null}

            <ReceiptBreakdownMiniGameSummary
              className={CLASS_NAME}
              kenoResult={kenoResult}
              ticketInfo={ticketInfo}
            />

            <hr />
            <div className={`${CLASS_NAME}__receipt-breakdown__section__footer`}>
              <div>Total</div>
              <TicketStatusPillWithWinnings
                totalWinnings={kenoResult.totalWinAmountInCents || null}
                type={'COMPLETED'}
                iconUrl={kenoResult.iconUrl}
              />
            </div>
          </div>
        </div>
      </ExpandableDrawer>
    </Dialog.Root>
  )
}
